import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import MedicationIcon from "@mui/icons-material/Medication";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import BadgeIcon from "@mui/icons-material/Badge";
import CreateIcon from "@mui/icons-material/Create";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import PaymentSummary from "../assign-prescriptions/counter/components/PaymentSummary";

const DeliveryReportOrderDetailsDialog = ({ open, onClose, popupData, t }) => {
  const isPaid = popupData && popupData.amount && popupData.amount.isPaid === true;
  const totalAmount = popupData && popupData.amount && popupData.amount.Total
    ? popupData.amount.Total
    : formatCurrency(
      popupData && popupData.items
        ? popupData.items.reduce((sum, item) => sum + (Number(item.price) || 0), 0)
        : 0
    );

  const capturedImages = useMemo(() => {
    if (!popupData || !Array.isArray(popupData.capturedImages)) {
      return [];
    }
    const seen = new Set();
    return popupData.capturedImages
      .map((image) => image && image.image_url)
      .filter((url) => {
        if (!url || seen.has(url)) {
          return false;
        }
        seen.add(url);
        return true;
      });
  }, [popupData]);

  const getConsultationStatus = () => {
    if (!popupData) return null;
    
    const consultations = popupData.consultations;
    
    if (!Array.isArray(consultations)) {
      return null;
    }
    
    if (consultations.length === 0) {
      return "pending";
    }
    
    const hasCompletedConsultation = consultations.some(
      (consultation) => {
        const status = (consultation && consultation.Status) || (consultation && consultation.status);
        return status && String(status).trim().toLowerCase() === "completed";
      }
    );
    
    return hasCompletedConsultation ? "done" : "pending";
  };

  const consultationStatus = getConsultationStatus();
  const idScanData = popupData && popupData.idData && popupData.idData.dlrawdata
    ? popupData.idData.dlrawdata
    : null;
  const isBedsideOrder = popupData && String(popupData.deliverytype || "").trim().toLowerCase() === "bedside";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center",
          pb: 2,
          pt: 3,
          background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
          color: "#ffffff",
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            borderRadius: "999px",
            px: 3,
            py: 1.25,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: "1.5rem",
              letterSpacing: "0.02em",
            }}
          >
            {t("Report.Delivered.OrderDetailsTitle", {
              defaultValue: "Order Details",
            })}
          </Typography>
          {popupData && popupData.storage_location ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.75,
                ml: 2,
                borderRadius: "999px",
                px: 2.5,
                py: 1,
                border: "1px solid rgba(255, 255, 255, 0.55)",
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.12) 100%)",
                boxShadow: "0 12px 28px rgba(15, 23, 42, 0.24)",
              }}
            >
              <Avatar
                sx={{
                  background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                  width: 36,
                  height: 36,
                  boxShadow: "0 6px 16px rgba(34, 197, 94, 0.35)",
                  border: "2px solid rgba(255, 255, 255, 0.75)",
                }}
              >
                <LocalPharmacyIcon sx={{ fontSize: 20, color: "#ffffff" }} />
              </Avatar>
              <Box sx={{ lineHeight: 1.05 }}>
                <Typography
                  component="span"
                  sx={{
                    display: "block",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.14em",
                    fontSize: "0.68rem",
                    color: "rgba(15, 23, 42, 0.7)",
                  }}
                >
                  {t("Console.ActionPopup.StorageLocation")}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    display: "block",
                    fontWeight: 800,
                    fontSize: "1.35rem",
                    color: "#0f172a",
                    letterSpacing: "-0.015em",
                  }}
                >
                  {popupData.storage_location}
                </Typography>
              </Box>
            </Box>
          ) : null}
        </Box>
        <Tooltip title={t("Console.ActionPopup.CancelTooltip")}>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              color: "#ffffff",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.2)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 3,
          backgroundColor: "#f8fafc",
          overflowX: "hidden",
        }}
      >
        {popupData ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  background: "#ffffff",
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 2.5,
                      pb: 2,
                      borderBottom: "2px solid #f1f5f9",
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: "#7c3aed",
                        mr: 1.5,
                        width: 32,
                        height: 32,
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        color: "#1e293b",
                      }}
                    >
                      {t("Console.ActionPopup.PatientInformation")}
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <InfoCell
                        label={t("Report.Delivered.PatientName", { defaultValue: "Patient" })}
                        value={[popupData.firstname, popupData.lastname].filter(Boolean).join(" ")}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <InfoCell
                        label={t("Report.Delivered.DateOfBirth", { defaultValue: "Date of Birth" })}
                        value={popupData.dob_str || "-"}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <InfoCell
                        label={t("Console.ActionPopup.StockCode")}
                        value={popupData.stock_code || "-"}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <InfoCell
                        label={t("Console.ActionPopup.PickupCode")}
                        value={popupData.pucode || "-"}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <InfoCell
                        label={t("Console.ActionPopup.StorageLocation")}
                        value={popupData.storage_location || "-"}
                      />
                    </Grid>
                    {isBedsideOrder ? (
                      <Grid item xs={6}>
                        <InfoCell
                          label={t("Console.Bedside.RoomNumber", { defaultValue: "Room Number" })}
                          value={popupData.room_number || "-"}
                        />
                      </Grid>
                    ) : null}
                    {isBedsideOrder ? (
                      <Grid item xs={12}>
                        <InfoCell
                          label={t("Assign.BedSide.OrderNotes", { defaultValue: "Order Notes" })}
                          value={popupData.order_notes || "-"}
                        />
                      </Grid>
                    ) : null}
                    <Grid item xs={6}>
                      <InfoCell
                        label={t("Report.Delivered.Store", { defaultValue: "Store" })}
                        value={popupData.store || "-"}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <InfoCell
                        label={t("Report.Delivered.Location", { defaultValue: "Location" })}
                        value={popupData.box || "-"}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <InfoCell
                        label={t("Report.Delivered.ContactlessPickup", { defaultValue: "Contactless" })}
                        value={popupData.iscontactlessactivated ? t("Global.Yes", { defaultValue: "Yes" }) : t("Global.No", { defaultValue: "No" })}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card
                elevation={0}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  background: "#ffffff",
                }}
              >
                <CardContent>
                  <SectionHeader 
                    icon={VideoCallIcon} 
                    title={t("Console.ActionPopup.Consultation", {
                      defaultValue: "Consultation",
                    })}
                  />
                  {consultationStatus ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 2.5,
                        backgroundColor:
                          consultationStatus === "done"
                            ? "#f0fdf4"
                            : "#fffbeb",
                        borderRadius: 2,
                        border: `2px solid ${
                          consultationStatus === "done"
                            ? "#22c55e"
                            : "#f59e0b"
                        }`,
                        boxShadow: `0 2px 8px ${
                          consultationStatus === "done"
                            ? "#22c55e20"
                            : "#f59e0b20"
                        }`,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            color: "#64748b",
                          }}
                        >
                          {t("Console.ActionPopup.ConsultationStatus", {
                            defaultValue: "Consultation Status",
                          })}:
                        </Typography>
                      </Box>
                      <Chip
                        icon={
                          consultationStatus === "done" ? (
                            <CheckCircleIcon />
                          ) : (
                            <ScheduleIcon />
                          )
                        }
                        label={
                          consultationStatus === "done"
                            ? t("Console.ActionPopup.ConsultationCompleted", {
                                defaultValue: "Completed",
                              })
                            : t("Console.ActionPopup.ConsultationPending", {
                                defaultValue: "Pending",
                              })
                        }
                        sx={{
                          backgroundColor:
                            consultationStatus === "done"
                              ? "#d1fae5"
                              : "#fef3c7",
                          color:
                            consultationStatus === "done"
                              ? "#166534"
                              : "#92400e",
                          fontWeight: 700,
                          fontSize: "0.875rem",
                          px: 2,
                          py: 1,
                          height: "auto",
                          "& .MuiChip-icon": {
                            color:
                              consultationStatus === "done"
                                ? "#166534"
                                : "#92400e",
                            fontSize: "1.25rem",
                          },
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        textAlign: "center",
                        py: 4,
                        backgroundColor: "#f8fafc",
                        borderRadius: 2,
                        border: "2px dashed #cbd5e1",
                      }}
                    >
                      <VideoCallIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 1 }} />
                      <Typography sx={{ color: "#94a3b8", fontSize: "0.875rem" }}>
                        {t("Report.Delivered.NoConsultation", {
                          defaultValue: "No consultation data available",
                        })}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>

              <Card
                elevation={0}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  background: "#ffffff",
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 2.5,
                      pb: 2,
                      borderBottom: "2px solid #f1f5f9",
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: "#7c3aed",
                        mr: 1.5,
                        width: 32,
                        height: 32,
                      }}
                    >
                      <MedicationIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        color: "#1e293b",
                      }}
                    >
                      {t("Report.Delivered.Items", { defaultValue: "Prescription Items" })}
                    </Typography>
                  </Box>
                  <Box sx={{ overflowX: "auto" }}>
                    <Table>
                      <TableHead>
                        <TableRow
                          sx={{
                            backgroundColor: "#f1f5f9",
                            "& .MuiTableCell-head": {
                              color: "#1e293b",
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                              borderBottom: "2px solid #cbd5e1",
                            },
                          }}
                        >
                          <TableCell>{t("Report.Delivered.RxNumber", { defaultValue: "Rx Number" })}</TableCell>
                          <TableCell>{t("Report.Delivered.DrugName", { defaultValue: "Drug Name" })}</TableCell>
                          <TableCell>{t("Report.Delivered.NDCCode", { defaultValue: "NDC Code" })}</TableCell>
                          <TableCell align="right">{t("Report.Delivered.Amount", { defaultValue: "Amount" })}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {popupData.items && popupData.items.length > 0 ? (
                          popupData.items.map((item) => (
                            <TableRow
                              key={item.id}
                              sx={{
                                "&:hover": {
                                  backgroundColor: "#f8fafc",
                                },
                              }}
                            >
                              <TableCell sx={{ color: "#1e293b", fontSize: "0.875rem" }}>
                                {item.rx_no}
                              </TableCell>
                              <TableCell
                                sx={{
                                  color: "#1e293b",
                                  fontSize: "0.875rem",
                                  fontWeight: 500,
                                }}
                              >
                                {item.drug_name}
                              </TableCell>
                              <TableCell sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                                {item.ndc_code}
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  sx={{
                                    color: "#1e293b",
                                    fontSize: "0.875rem",
                                    fontWeight: 600,
                                  }}
                                >
                                  {formatCurrency(item.price)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={4} align="center" sx={{ py: 4, color: "#94a3b8" }}>
                              {t("Report.Delivered.NoItems", { defaultValue: "No prescription items." })}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  background: "#ffffff",
                }}
              >
                <CardContent>
                  <PaymentSummary t={t} isPaid={isPaid} totalAmount={totalAmount} />
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={6}>
                      <InfoCell
                        label={t("Report.Delivered.OrderCreatedOn", { defaultValue: "Created On" })}
                        value={formatDate(popupData.order_created_on)}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <InfoCell
                        label={t("Report.Delivered.PickupDate", { defaultValue: "Pickup Date" })}
                        value={formatDate(popupData.pickupdate)}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card
                elevation={0}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  background: "#ffffff",
                  height: "320px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  <SectionHeader icon={BadgeIcon} title={t("Report.Delivered.IDCapture", { defaultValue: "ID Capture" })} />
                  <Box sx={{ flex: 1, overflow: "auto" }}>
                    {capturedImages.length > 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          flexWrap: "wrap",
                          maxHeight: "240px",
                          overflowY: "auto",
                          "&::-webkit-scrollbar": {
                            width: "8px",
                          },
                          "&::-webkit-scrollbar-track": {
                            background: "#f1f5f9",
                            borderRadius: "4px",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            background: "#7c3aed",
                            borderRadius: "4px",
                            "&:hover": {
                              background: "#6d28d9",
                            },
                          },
                        }}
                      >
                        {capturedImages.map((url, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              flex: "1 1 calc(50% - 8px)",
                              minWidth: "200px",
                              border: "2px solid #e2e8f0",
                              borderRadius: 2,
                              overflow: "hidden",
                              p: 1,
                              backgroundColor: "#f8fafc",
                              height: "200px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <img
                              src={url}
                              alt={`ID Capture ${idx + 1}`}
                              style={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                                display: "block",
                              }}
                              draggable={false}
                            />
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 4,
                          backgroundColor: "#f8fafc",
                          borderRadius: 2,
                          border: "2px dashed #cbd5e1",
                        }}
                      >
                        <BadgeIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 1 }} />
                        <Typography sx={{ color: "#94a3b8", fontSize: "0.875rem" }}>
                          {t("Report.Delivered.NoIdImages", { defaultValue: "No ID captured" })}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>

              {idScanData && (
                <Card
                  elevation={0}
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    border: "1px solid #e2e8f0",
                    background: "#ffffff",
                  }}
                >
                  <CardContent>
                    <SectionHeader
                      icon={QrCodeScannerIcon}
                      title={t("Report.Delivered.IDScan", {
                        defaultValue: "ID Scan",
                      })}
                    />
                    {popupData.idData ? (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        {idScanData && (
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: "#64748b",
                                fontSize: "0.75rem",
                                mb: 1.5,
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                              }}
                            >
                              {t("Report.Delivered.RawScanData", {
                                defaultValue: "Raw Scan Data",
                              })}
                            </Typography>
                            <TextField
                              fullWidth
                              multiline
                              rows={6}
                              value={idScanData}
                              InputProps={{
                                readOnly: true,
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  backgroundColor: "#f8fafc",
                                  fontSize: "0.75rem",
                                  fontFamily: "monospace",
                                  "& fieldset": {
                                    borderColor: "#e2e8f0",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: "#cbd5e1",
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#7c3aed",
                                  },
                                },
                              }}
                            />
                          </Box>
                        )}
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "#64748b",
                              fontSize: "0.75rem",
                              mb: 1.5,
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                            }}
                          >
                            {t("Report.Delivered.ParsedData", {
                              defaultValue: "Parsed Data",
                            })}
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <InfoCell
                                label={t("Report.Delivered.FirstName", {
                                  defaultValue: "First Name",
                                })}
                                value={popupData.idData.first_name || "-"}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <InfoCell
                                label={t("Report.Delivered.LastName", {
                                  defaultValue: "Last Name",
                                })}
                                value={popupData.idData.last_name || "-"}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <InfoCell
                                label={t("Report.Delivered.DriversLicenseNo", {
                                  defaultValue: "Driver's License No",
                                })}
                                value={popupData.idData.drivers_license_no || "-"}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <InfoCell
                                label={t("Report.Delivered.DateOfBirth", {
                                  defaultValue: "Date of Birth",
                                })}
                                value={popupData.idData.dob || "-"}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <InfoCell
                                label={t("Report.Delivered.ExpirationDate", {
                                  defaultValue: "Expiration Date",
                                })}
                                value={popupData.idData.expdate || "-"}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <InfoCell
                                label={t("Report.Delivered.StateCode", {
                                  defaultValue: "State Code",
                                })}
                                value={popupData.idData.selectedstatecode || "-"}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <InfoCell
                                label={t("Report.Delivered.Relationship", {
                                  defaultValue: "Relationship",
                                })}
                                value={popupData.idData.relationship || "-"}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <InfoCell
                                label={t("Report.Delivered.CardType", {
                                  defaultValue: "Card Type",
                                })}
                                value={popupData.idData.card_type || "-"}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 4,
                          backgroundColor: "#f8fafc",
                          borderRadius: 2,
                          border: "2px dashed #cbd5e1",
                        }}
                      >
                        <QrCodeScannerIcon
                          sx={{ fontSize: 48, color: "#cbd5e1", mb: 1 }}
                        />
                        <Typography sx={{ color: "#94a3b8", fontSize: "0.875rem" }}>
                          {t("Report.Delivered.NoIdScanData", {
                            defaultValue: "No ID scan data available",
                          })}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}

              <Card
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  background: "#ffffff",
                }}
              >
                <CardContent>
                  <SectionHeader icon={CreateIcon} title={t("Console.ActionPopup.Signature") || "Signature"} />
                  {popupData.psignature_url || popupData.signatureurl ? (
                    <Box
                      sx={{
                        border: "2px solid #e2e8f0",
                        borderRadius: 2,
                        overflow: "hidden",
                        p: 2,
                        backgroundColor: "#f8fafc",
                        textAlign: "center",
                        height: "180px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={popupData.psignature_url || popupData.signatureurl}
                        alt="signature"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        textAlign: "center",
                        py: 4,
                        backgroundColor: "#f8fafc",
                        borderRadius: 2,
                        border: "2px dashed #cbd5e1",
                        height: "180px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CreateIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 1 }} />
                      <Typography sx={{ color: "#94a3b8", fontSize: "0.875rem" }}>
                        {t("Report.Delivered.NoSignature", { defaultValue: "No signature available" })}
                      </Typography>
                    </Box>
                  )}
                  {(() => {
                    const raw = (popupData && (popupData.signed_pdf_url || popupData.signedPdfUrl || popupData.signature_signed_pdf_url)) || "";
                    if (!raw || typeof raw !== "string") return null;
                    const base = (process.env.REACT_APP_BASEURL || "").replace(/\/+$/, "") + (process.env.REACT_APP_BASEURL ? "/" : "");
const fullUrl = raw.indexOf("http") === 0 ? raw : base + (raw.charAt(0) === "/" ? raw.slice(1) : raw);
                    return (
                      <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #e2e8f0" }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#64748b", fontSize: "0.75rem", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          {t("Report.Delivered.SignedPdfUrl", { defaultValue: "Signed PDF (full URL)" })}
                        </Typography>
                        <Link href={fullUrl} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: "break-all", fontSize: "0.875rem" }}>
                          {fullUrl}
                        </Link>
                      </Box>
                    );
                  })()}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

const InfoCell = ({ label, value }) => (
  <Box>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600,
        color: "#64748b",
        fontSize: "0.75rem",
        mb: 0.5,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        color: "#1e293b",
        fontWeight: 600,
        fontSize: "0.95rem",
      }}
    >
      {value || "-"}
    </Typography>
  </Box>
);

InfoCell.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

InfoCell.defaultProps = {
  value: "-",
};

const SectionHeader = ({ icon: IconComponent, title }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      pb: 2,
      mb: 2,
      borderBottom: "2px solid #f1f5f9",
      gap: 1.5,
    }}
  >
    <Avatar
      sx={{
        bgcolor: "#7c3aed",
        width: 32,
        height: 32,
      }}
    >
      <IconComponent sx={{ fontSize: 20 }} />
    </Avatar>
    <Typography
      variant="h6"
      sx={{
        fontWeight: 700,
        fontSize: "1.1rem",
        color: "#1e293b",
      }}
    >
      {title}
    </Typography>
  </Box>
);

SectionHeader.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
};

const formatCurrency = (value) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return "$0.00";
  }
  return `$${numeric.toFixed(2)}`;
};

const formatDate = (value) => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
};

DeliveryReportOrderDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  popupData: PropTypes.object,
  t: PropTypes.func,
};

DeliveryReportOrderDetailsDialog.defaultProps = {
  popupData: null,
  t: (key, opts) => (opts && opts.defaultValue ? opts.defaultValue : key),
};

export default DeliveryReportOrderDetailsDialog;



// WEBPACK FOOTER //
// ./src/components/Delivery-Report/OrderDetailsDialog.jsx